import { useMemo, useState } from "react";
import { AlertCircle, Edit, FileText, Printer, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAtestados, preencherModeloAtestado } from "@/hooks/useAtestados";
import { useDentistas } from "@/hooks/useDentistas";
import { useClinicaDocumento } from "@/hooks/useClinicaDocumento";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { usePacientes } from "@/hooks/usePacientes";
import { parseLocalDate, todayLocalISO } from "@/lib/date-utils";
import { printAtestadoModelo } from "@/lib/documentPrint";
import { EnviarDocumentoPaciente } from "@/components/documentos/EnviarDocumentoPaciente";

export function Atestados() {
  const { pacientes } = usePacientes();
  const { dentistas } = useDentistas();
  const { informacoes } = useInformacoesClinica();
  const { activeClinicaId, clinicaDocumento } = useClinicaDocumento(informacoes);
  const {
    modelos,
    documentos,
    seedModelosPadrao,
    createModelo,
    updateModelo,
    deleteModelo,
    createDocumento,
  } = useAtestados();
  const [pacienteId, setPacienteId] = useState("");
  const [modeloId, setModeloId] = useState("");
  const [profissionalId, setProfissionalId] = useState("");
  const [periodo, setPeriodo] = useState("1 hora");
  const [diasRepouso, setDiasRepouso] = useState("1");
  const [cpf, setCpf] = useState("");
  const [cid, setCid] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFim, setHoraFim] = useState("");
  const [observacoesAtestado, setObservacoesAtestado] = useState("");
  const [dataEmissao, setDataEmissao] = useState(todayLocalISO);
  const [conteudoLivre, setConteudoLivre] = useState("");
  const [novoModelo, setNovoModelo] = useState({ titulo: "", tipo: "", conteudo: "" });
  const [editingModeloId, setEditingModeloId] = useState<string | null>(null);
  const [editingModelo, setEditingModelo] = useState({ titulo: "", tipo: "", conteudo: "", ativo: true });

  const paciente = pacientes.find((item) => item.id === pacienteId);
  const modelo = modelos.find((item) => item.id === modeloId);
  const profissional = dentistas.find((item) => item.id === profissionalId);

  const conteudo = useMemo(() => {
    const base = conteudoLivre || modelo?.conteudo || "";
    return preencherModeloAtestado(base, {
      paciente: paciente?.nome || "",
      data: dataEmissao.split("-").reverse().join("/"),
      periodo,
      dias_repouso: diasRepouso,
      cpf,
      cid,
      hora_inicio: horaInicio,
      hora_fim: horaFim,
      observacoes_atestado: observacoesAtestado,
      profissional: profissional?.nome || "",
      cro: profissional?.cro || "",
    });
  }, [conteudoLivre, modelo?.conteudo, paciente?.nome, dataEmissao, periodo, diasRepouso, cpf, cid, horaInicio, horaFim, observacoesAtestado, profissional?.nome, profissional?.cro]);

  const documentosPaciente = documentos.filter((item) => !pacienteId || item.paciente_id === pacienteId);

  const emitir = async () => {
    if (!pacienteId || !conteudo.trim()) {
      toast.error("Selecione o paciente e preencha o texto do documento.");
      return;
    }
    const titulo = modelo?.titulo || "Documento odontológico";
    const doc = await createDocumento({
      paciente_id: pacienteId,
      clinica_id: activeClinicaId,
      clinica_dados: clinicaDocumento() || undefined,
      modelo_id: modeloId || undefined,
      tipo: modelo?.tipo || "Documento",
      titulo,
      conteudo,
      profissional: profissional?.nome || "",
      cro: profissional?.cro || "",
      data_emissao: dataEmissao,
      status: "Emitido",
    });
    if (doc) printAtestadoModelo({
      titulo,
      conteudo,
      pacienteNome: paciente?.nome,
      profissional: profissional?.nome,
      cro: profissional?.cro,
      clinica: clinicaDocumento(doc),
    });
  };

  const salvarModelo = async () => {
    if (!novoModelo.titulo || !novoModelo.conteudo) {
      toast.error("Informe título e conteúdo do modelo.");
      return;
    }
    await createModelo({ ...novoModelo, ativo: true, tipo: novoModelo.tipo || "Documento" });
    setNovoModelo({ titulo: "", tipo: "", conteudo: "" });
  };

  const startEditModelo = (item: any) => {
    setEditingModeloId(item.id);
    setEditingModelo({
      titulo: item.titulo || "",
      tipo: item.tipo || "",
      conteudo: item.conteudo || "",
      ativo: item.ativo !== false,
    });
  };

  const salvarEdicaoModelo = async () => {
    if (!editingModeloId || !editingModelo.titulo || !editingModelo.conteudo) {
      toast.error("Informe título e conteúdo do modelo.");
      return;
    }
    const saved = await updateModelo(editingModeloId, {
      ...editingModelo,
      tipo: editingModelo.tipo || "Documento",
    });
    if (saved) {
      setEditingModeloId(null);
      setEditingModelo({ titulo: "", tipo: "", conteudo: "", ativo: true });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold sm:text-3xl"><FileText className="h-7 w-7" /> Atestados e documentos</h1>
          <p className="text-muted-foreground">Emita atestados, declarações e documentos vinculados ao paciente.</p>
        </div>

        {(pacientes.length === 0 || modelos.length === 0) && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Para emitir um documento, cadastre um paciente em Cadastros &gt; Pacientes e crie os modelos nesta tela. Se quiser começar rápido, use o botão "Criar modelos padrão".
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="emitir">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="emitir">Emitir</TabsTrigger>
            <TabsTrigger value="modelos">Modelos</TabsTrigger>
            <TabsTrigger value="historico">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="emitir" className="mt-4">
            <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
              <Card>
                <CardHeader>
                  <CardTitle>Dados do documento</CardTitle>
                  <CardDescription>Escolha paciente, modelo e profissional.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Paciente</Label>
                    <Select value={pacienteId} onValueChange={setPacienteId}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{pacientes.map((item) => <SelectItem key={item.id} value={item.id}>{item.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Modelo</Label>
                    <Select value={modeloId} onValueChange={(value) => { setModeloId(value); setConteudoLivre(""); }}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{modelos.filter((item) => item.ativo).map((item) => <SelectItem key={item.id} value={item.id}>{item.titulo}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Profissional</Label>
                    <Select value={profissionalId} onValueChange={setProfissionalId}>
                      <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                      <SelectContent>{dentistas.map((item) => <SelectItem key={item.id} value={item.id}>{item.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input type="date" value={dataEmissao} onChange={(e) => setDataEmissao(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Repouso</Label>
                      <Input value={diasRepouso} onChange={(e) => setDiasRepouso(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Hora início</Label>
                      <Input type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Hora fim</Label>
                      <Input type="time" value={horaFim} onChange={(e) => setHoraFim(e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Período de permanência</Label>
                    <Input value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>CPF</Label>
                      <Input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="Livre" />
                    </div>
                    <div className="space-y-2">
                      <Label>CID</Label>
                      <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Opcional" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Obs. do atestado</Label>
                    <Input value={observacoesAtestado} onChange={(e) => setObservacoesAtestado(e.target.value)} placeholder="Opcional" />
                  </div>
                  <Button onClick={emitir} className="w-full"><Printer className="mr-2 h-4 w-4" /> Emitir e imprimir</Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Texto final</CardTitle>
                  <CardDescription>Edite antes de emitir quando precisar.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea value={conteudoLivre || modelo?.conteudo || ""} onChange={(e) => setConteudoLivre(e.target.value)} className="min-h-[220px]" />
                  <div className="rounded-md border bg-muted/30 p-4 text-sm leading-6 whitespace-pre-wrap">{conteudo || "O texto preenchido aparece aqui."}</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="modelos" className="mt-4">
            <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
              <Card>
                <CardHeader><CardTitle>Novo modelo</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Input placeholder="Título" value={novoModelo.titulo} onChange={(e) => setNovoModelo({ ...novoModelo, titulo: e.target.value })} />
                  <Input placeholder="Tipo" value={novoModelo.tipo} onChange={(e) => setNovoModelo({ ...novoModelo, tipo: e.target.value })} />
                  <Textarea placeholder="Use {paciente}, {cpf}, {data}, {periodo}, {hora_inicio}, {hora_fim}, {dias_repouso}, {cid}, {observacoes_atestado}, {profissional}, {cro}" value={novoModelo.conteudo} onChange={(e) => setNovoModelo({ ...novoModelo, conteudo: e.target.value })} />
                  <Button onClick={salvarModelo} className="w-full"><Save className="mr-2 h-4 w-4" /> Salvar modelo</Button>
                  <Button onClick={seedModelosPadrao} variant="outline" className="w-full"><Plus className="mr-2 h-4 w-4" /> Criar modelos padrão</Button>
                </CardContent>
              </Card>
              <div className="space-y-3">
                {modelos.map((item) => (
                  <Card key={item.id}>
                    <CardContent className="p-4">
                      {editingModeloId === item.id ? (
                        <div className="space-y-3">
                          <Input value={editingModelo.titulo} onChange={(e) => setEditingModelo({ ...editingModelo, titulo: e.target.value })} />
                          <Input value={editingModelo.tipo} onChange={(e) => setEditingModelo({ ...editingModelo, tipo: e.target.value })} />
                          <Textarea value={editingModelo.conteudo} onChange={(e) => setEditingModelo({ ...editingModelo, conteudo: e.target.value })} className="min-h-[140px]" />
                          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                            <Button variant="outline" onClick={() => setEditingModeloId(null)}><X className="mr-2 h-4 w-4" /> Cancelar</Button>
                            <Button onClick={salvarEdicaoModelo}><Save className="mr-2 h-4 w-4" /> Salvar alterações</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2"><strong>{item.titulo}</strong><Badge variant="secondary">{item.tipo}</Badge></div>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{item.conteudo}</p>
                          </div>
                          <div className="flex gap-2 sm:shrink-0">
                            <Button variant="ghost" size="icon" onClick={() => startEditModelo(item)} title="Editar modelo"><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteModelo(item.id)} title="Excluir modelo"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="historico" className="mt-4">
            <div className="space-y-3">
              {documentosPaciente.length === 0 ? (
                <Card><CardContent className="p-6 text-sm text-muted-foreground">Nenhum documento emitido.</CardContent></Card>
              ) : documentosPaciente.map((item) => (
                <Card key={item.id}>
                  <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <strong>{item.titulo}</strong>
                      <p className="text-sm text-muted-foreground">{parseLocalDate(item.data_emissao).toLocaleDateString("pt-BR")} - {pacientes.find((p) => p.id === item.paciente_id)?.nome || "Paciente"}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                    <EnviarDocumentoPaciente entidade="documentos_emitidos" documentoId={item.id} clinicaId={item.clinica_id || activeClinicaId} titulo={item.titulo} paciente={pacientes.find((p) => p.id === item.paciente_id)} />
                    <Button variant="outline" onClick={() => printAtestadoModelo({
                      titulo: item.titulo,
                      conteudo: item.conteudo,
                      pacienteNome: pacientes.find((p) => p.id === item.paciente_id)?.nome,
                      profissional: item.profissional,
                      cro: item.cro,
                      clinica: clinicaDocumento(item),
                    })}><Printer className="mr-2 h-4 w-4" /> Imprimir</Button></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
